import { useState } from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import { LearnSection } from '../../../constants/learn';
import { UNSTYLED_BUTTON_SX } from '../../../constants/ui';

interface Props {
  section: LearnSection;
  isOpen: boolean;
  onOpen: () => void;
}

function LibrarySection({ section, isOpen, onOpen }: Props) {
  const theme = useTheme();
  const [activeIndex, setActiveIndex] = useState(0);

  const Icon = section.icon;
  const active = section.lessons[activeIndex];

  const heading = (
    <>
      <Icon
        sx={{ fontSize: 19, color: 'text.primary' }}
        // The section title already names it.
        aria-hidden
      />
      <Typography variant="h4" component="h2" lineHeight={1.2}>
        {section.title}
      </Typography>
    </>
  );

  return (
    <Box
      borderRadius="10px"
      overflow="hidden"
      border={`1px solid ${theme.palette.lineGrey.main}`}
      bgcolor="white">
      {!isOpen && (
        <Box
          component="button"
          type="button"
          onClick={onOpen}
          sx={{
            ...UNSTYLED_BUTTON_SX,
            padding: theme.spacing(2.75, 3),
            ':hover': { backgroundColor: theme.palette.lightGrey.main },
          }}>
          <Stack direction="row" alignItems="center" gap={1.5}>
            {heading}
            <ChevronRightRounded
              sx={{
                fontSize: 18,
                marginLeft: 'auto',
                color: theme.palette.outlineGrey.main,
              }}
            />
          </Stack>
        </Box>
      )}

      {isOpen && (
        <Box
          display="grid"
          // Two columns only once the sidebar and padding still leave the
          // player a usable width; below that the panel stacks.
          gridTemplateColumns={{
            xs: 'minmax(0, 1fr)',
            lg: 'minmax(300px, 380px) minmax(0, 1fr)',
          }}>
          <Box p={3}>
            <Stack direction="row" alignItems="center" gap={1.5} mb={2.5}>
              {heading}
            </Stack>
            <Stack gap="2px">
              {section.lessons.map((lesson, index) => (
                <Box
                  key={lesson.title}
                  component="button"
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  sx={{
                    ...UNSTYLED_BUTTON_SX,
                    borderRadius: '5px',
                    padding: theme.spacing(1.75, 2),
                    backgroundColor:
                      index === activeIndex
                        ? theme.palette.lightGrey.main
                        : 'transparent',
                    ':hover': { backgroundColor: theme.palette.lightGrey.main },
                  }}>
                  <Typography
                    variant={index === activeIndex ? 'h5' : 'body2'}
                    component="p">
                    {lesson.title}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          <Box
            bgcolor={theme.palette.lightGrey.main}
            borderLeft={{
              xs: 'none',
              lg: `1px solid ${theme.palette.lineGrey.main}`,
            }}
            borderTop={{
              xs: `1px solid ${theme.palette.lineGrey.main}`,
              lg: 'none',
            }}>
            <Box p={3}>
              <Box
                position="relative"
                overflow="hidden"
                sx={{
                  aspectRatio: '16 / 9',
                  border: '1px solid black',
                  borderRadius: '10px',
                  backgroundColor: theme.palette.lineGrey.main,
                }}>
                <Box
                  component="iframe"
                  key={activeIndex}
                  src={active.url}
                  title={active.title}
                  loading="lazy"
                  allowFullScreen
                  allow="encrypted-media; fullscreen; microphone; screen-wake-lock;"
                  sx={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                />
              </Box>
            </Box>

            <Box pt={0.5} pb={3.5} px={3}>
              <Typography variant="h3" component="p" mb={1}>
                {active.title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                lineHeight={1.5}
                maxWidth="520px">
                {active.description}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default LibrarySection;
